import { FormControl, FormLabel } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { FieldPath, PathValue, useFormContext } from 'react-hook-form';

import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { StrictFieldValues } from '~/core/routes/settings/implementations/common/Inputs/types';

// This type only allows react-hook-form paths that reference string[] values.
type StringArrayPath<T extends StrictFieldValues> = {
  [K in FieldPath<T>]: PathValue<T, K> extends readonly string[] ? K : never;
}[FieldPath<T>];

type IsolatedMultiSelectInputProps<T extends StrictFieldValues> = {
  counterLabel?: string;
  label?: string;
  name: StringArrayPath<T>;
  options: { label: React.ReactNode; value: string }[];
  placeholder?: string;
  titleText: string;
};

export const IsolatedMultiSelectInput = <T extends StrictFieldValues>({
  counterLabel,
  label,
  name,
  options,
  placeholder = '',
  titleText,
}: IsolatedMultiSelectInputProps<T>) => {
  const { register, setValue, getValues } = useFormContext<T>();

  useEffect(() => {
    register(name);
  }, [name, register]);

  return (
    <FormControl>
      {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
      <IsolatedMultiSelect
        autoApplyOptions
        buttonProps={{
          width: ['100%'],
        }}
        counterLabel={counterLabel}
        // can cast to string[] here because of the StringArrayPath type
        initialSelections={getValues(name) as string[]}
        isSearchable={options.length > 5}
        placeholderText={placeholder}
        options={options}
        onApplyOptions={(selected: string[]) => {
          // casting to any here is ok because StringArrayPath ensures that the value
          // of this form field will be string[]
          setValue(name, selected as any);
        }}
        showSelectedCount
        titleText={titleText}
      />
    </FormControl>
  );
};
